import React, { useEffect, useState, useCallback } from "react";
import { storeApi, getCurrentBusinessId } from "../../services/api";
import { Store } from "../../services/api/types";
import { useTranslation } from "react-i18next";

interface StoreFormData {
  name: string;
  manager: string;
  email: string;
  phone: string;
  website: string;
  storeType: string;
  password: string;
  confirmPassword: string;
  requirePasswordChange: boolean;
  addressType: 'simple' | 'detailed';
  simpleAddress: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: number;
  longitude: number;
  gpsEnabled: boolean;
  gpsRadius: number;
  allowChipActivation: boolean;
  allowQRActivation: boolean;
}

const STORE_TYPES = [
  { value: 'retailer', label: 'Retailer' },
  { value: 'agent', label: 'Agent / Distributor' },
  { value: 'flagship', label: 'Flagship Store' },
  { value: 'factory', label: 'Factory' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'outlet', label: 'Outlet' },
];

// Auto-detect GPS from browser/phone
const autoDetectGPS = (
  onSuccess: (lat: number, lng: number) => void,
  onError?: (msg: string) => void
) => {
  if (!navigator.geolocation) {
    onError?.('Geolocation is not supported by your browser');
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (position) => {
      onSuccess(
        Math.round(position.coords.latitude * 10000) / 10000,
        Math.round(position.coords.longitude * 10000) / 10000
      );
    },
    (err) => {
      onError?.(err.message || 'Unable to get your location');
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
};

const validatePassword = (password: string): { valid: boolean; error?: string } => {
  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters long' };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one uppercase letter' };
  }
  
  if (!/[a-z]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one lowercase letter' };
  }
  
  if (!/[0-9]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one number' };
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { valid: false, error: 'Password must contain at least one special character (!@#$%^&*...)' };
  }
  
  return { valid: true };
};

const StoresPage = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { t } = useTranslation();

  const [formData, setFormData] = useState<StoreFormData>({
    name: "",
    manager: "",
    email: "",
    phone: "",
    website: "",
    storeType: "retailer",
    password: "",
    confirmPassword: "",
    requirePasswordChange: true,
    addressType: 'simple',
    simpleAddress: "",
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
    latitude: 0,
    longitude: 0,
    gpsEnabled: true,
    gpsRadius: 50000,
    allowChipActivation: true,
    allowQRActivation: true,
  });

  const loadStores = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading stores from API...');
      const businessId = getCurrentBusinessId();
      console.log('📋 Current Business ID:', businessId);
      
      if (!businessId) {
        throw new Error('No business ID found. Please log in again.');
      }
      
      const response = await storeApi.getStores({ businessId });
      console.log('🔍 Raw API response:', response);
      
      let storesData: any[] = [];
      
      if (response && response.success && response.data) {
        storesData = Array.isArray(response.data) ? response.data : [];
      } else if (response && response.success && (response as any).stores) {
        storesData = Array.isArray((response as any).stores) ? (response as any).stores : [];
      } else if (Array.isArray(response)) {
        storesData = response;
      } else if (response && typeof response === 'object' && 'stores' in response) {
        storesData = Array.isArray((response as any).stores) ? (response as any).stores : [];
      }
      
      console.log('📦 Stores data extracted:', storesData);
      console.log('📦 Number of stores found:', storesData.length);
      
      if (storesData.length > 0) {
        const normalizedStores = storesData.map((store: any) => ({
          ...store,
          storeId: store.storeId || String(store._id || ''),
          storeName: store.storeName || store.name || `Store ${String(store._id || '').slice(-6)}`,
          manager: store.manager || 'Not assigned',
          email: store.email || '',
          businessId: store.businessId,
          storePhone: store.storePhone || store.phone || '',
          isActive: store.isActive !== false,
          status: store.isActive !== false ? 'active' : 'inactive',
          latitude: store.latitude ||
                   (store.location?.coordinates?.[1]) ||
                   0,
          longitude: store.longitude ||
                    (store.location?.coordinates?.[0]) ||
                    0,
          // address can be string or object — normalize both
          address: typeof store.address === 'object' && store.address
            ? `${store.address.street || ''} ${store.address.city || ''}`.trim()
            : (store.address || ''),
          city: store.city || (typeof store.address === 'object' ? store.address?.city : '') || '',
          state: store.state || (typeof store.address === 'object' ? store.address?.state : '') || '',
          postalCode: store.postalCode || (typeof store.address === 'object' ? store.address?.postalCode : '') || '',
          country: store.country || (typeof store.address === 'object' ? store.address?.country : '') || 'US',
          gpsValidation: store.gpsValidation || { enabled: false, radius: 50000 },
          allowChipActivation: store.allowChipActivation !== false,
          allowQRActivation: store.allowQRActivation !== false,
          storeType: store.storeType || 'retailer',
        }));
        
        setStores(normalizedStores);
        console.log(`✅ Loaded ${normalizedStores.length} stores successfully`);
        
        if ((response as any).pagination) {
          console.log('📄 Pagination:', (response as any).pagination);
        }
      } else {
        console.warn('⚠️ No stores found or invalid response structure');
        setStores([]);
        
        if (response && !response.success) {
          setError(response.error || response.message || 'Failed to load stores');
        }
      }
      
    } catch (err: any) {
      console.error('❌ Failed to load stores:', err);
      console.error('❌ Error details:', err.response || err);
      
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view stores.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(`Failed to load stores: ${err.message}`);
      } else {
        setError('An unexpected error occurred while loading stores.');
      }
      
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [refreshKey, loadStores]);

  useEffect(() => {
    console.log('=== StoresPage Debug Info ===');
    console.log('Component mounted');
    console.log('Current stores:', stores);
    console.log('Loading state:', loading);
    console.log('Error state:', error);
    console.log('Auth token:', localStorage.getItem('authToken') || localStorage.getItem('businessToken') || 'No token found');
    console.log('Business data:', localStorage.getItem('businessData'));
    console.log('===========================');
  }, [stores, loading, error]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.manager.trim() || !formData.email.trim() || !formData.storeType) {
      alert(t('stores.validation.requiredFields') || 'Please fill in all required fields (Name, Manager, Email, Store Type, Password)');
      return;
    }

    if (!formData.password.trim()) {
      alert(t('stores.validation.passwordRequired') || 'Password is required');
      return;
    }

    const passwordCheck = validatePassword(formData.password);
    if (!passwordCheck.valid) {
      alert(passwordCheck.error);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      alert(t('stores.validation.passwordMismatch') || 'Passwords do not match');
      return;
    }

    if (formData.addressType === 'simple' && !formData.simpleAddress.trim()) {
      alert(t('stores.validation.locationRequired') || 'Please enter a location/city for the store');
      return;
    }

    if (formData.latitude < -90 || formData.latitude > 90) {
      alert(t('stores.validation.invalidLatitude') || 'Latitude must be between -90 and 90');
      return;
    }
    
    if (formData.longitude < -180 || formData.longitude > 180) {
      alert(t('stores.validation.invalidLongitude') || 'Longitude must be between -180 and 180');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert(t('stores.validation.invalidEmail') || 'Please enter a valid email address');
      return;
    }
    
    try {
      const currentBusinessId = getCurrentBusinessId();
      
      if (!currentBusinessId) {
        throw new Error('No business ID found. Please log in again.');
      }
      
      let storeData: any = {
        businessId: currentBusinessId,
        storeName: formData.name.trim(),
        manager: formData.manager.trim(),
        email: formData.email.trim(),
        password: formData.password,
        requirePasswordChange: formData.requirePasswordChange,
        storePhone: formData.phone.trim() || undefined,
        website: formData.website.trim() || undefined,
        storeType: formData.storeType,
        latitude: formData.latitude,
        longitude: formData.longitude,
        gpsValidation: {
          enabled: formData.gpsEnabled,
          radius: formData.gpsRadius,
          strictMode: false,
        },
        allowChipActivation: formData.allowChipActivation,
        allowQRActivation: formData.allowQRActivation,
      };

      if (formData.addressType === 'simple') {
        storeData.address = formData.simpleAddress.trim();
      } else {
        storeData.address = {
          street: formData.street.trim() || undefined,
          city: formData.city.trim() || undefined,
          state: formData.state.trim() || undefined,
          postalCode: formData.postalCode.trim() || undefined,
          country: formData.country || "US",
        };
        
        storeData.city = formData.city.trim() || undefined;
        storeData.state = formData.state.trim() || undefined;
        storeData.postalCode = formData.postalCode.trim() || undefined;
        storeData.country = formData.country || "US";
      }

      console.log('🚀 Creating store:', { ...storeData, password: '***HIDDEN***' });
      const response = await storeApi.createStore(storeData);
      
      if (response.success) {
        console.log("✅ Store created successfully!");
        
        // ⭐ FIX: Cast response to access additional properties
        const apiResponse = response as any;
        
        const credentials = apiResponse.credentials || {
          storeId: apiResponse.store?.storeId || apiResponse.data?.storeId,
          temporaryPassword: formData.password
        };
        
        alert(`${t('stores.success.created') || 'Store created successfully'}!

🔐 ${t('stores.credentials.title') || 'IMPORTANT - Save these credentials'}:
━━━━━━━━━━━━━━━━━━━━━━━━━━━
${t('stores.credentials.storeId') || 'Store ID'}: ${credentials.storeId}
${t('stores.credentials.password') || 'Temporary Password'}: ${credentials.temporaryPassword}
━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚠️ ${t('stores.credentials.warning') || 'The store manager must change this password on first login'}.

${t('stores.credentials.share') || 'Provide these credentials to'} ${formData.manager} (${formData.email})`);
        
        resetForm();
        setShowCreateModal(false);
        setRefreshKey(prev => prev + 1);
      } else {
        throw new Error(response.error || 'Failed to create store');
      }
      
    } catch (err: any) {
      console.error('❌ Error creating store:', err);
      alert(`${t('stores.error.createFailed') || 'Failed to create store'}: ${err.message}`);
    }
  };

  const openEditModal = (store: Store) => {
    setEditingStore(store);
    
    let addressType: 'simple' | 'detailed' = 'simple';
    let simpleAddress = "";
    let street = "", city = "", state = "", postalCode = "", country = "US";

    if (typeof store.address === 'string') {
      addressType = 'simple';
      simpleAddress = store.address;
    } else if (typeof store.address === 'object' && store.address) {
      addressType = 'detailed';
      street = store.address.street || "";
      city = store.address.city || store.city || "";
      state = store.address.state || store.state || "";
      postalCode = store.address.postalCode || store.postalCode || "";
      country = store.address.country || store.country || "US";
    } else if (store.city || store.state) {
      addressType = 'detailed';
      city = store.city || "";
      state = store.state || "";
      postalCode = store.postalCode || "";
      country = store.country || "US";
    }
    
    setFormData({
      name: store.storeName || "",
      manager: store.manager || "",
      email: store.email || "",
      phone: store.storePhone || "",
      website: (store as any).website || "",
      storeType: (store as any).storeType || 'retailer',
      password: "",
      confirmPassword: "",
      requirePasswordChange: true,
      addressType,
      simpleAddress,
      street,
      city,
      state,
      postalCode,
      country,
      latitude: store.latitude || 0,
      longitude: store.longitude || 0,
      gpsEnabled: store.gpsValidation?.enabled || false,
      gpsRadius: store.gpsValidation?.radius || 50000,
      allowChipActivation: (store as any).allowChipActivation !== false,
      allowQRActivation: (store as any).allowQRActivation !== false,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingStore) return;

    if (!formData.name.trim() || !formData.manager.trim() || !formData.email.trim()) {
      alert(t('stores.validation.requiredFields') || 'Please fill in all required fields');
      return;
    }

    if (formData.latitude < -90 || formData.latitude > 90) {
      alert(t('stores.validation.invalidLatitude') || 'Latitude must be between -90 and 90');
      return;
    }
    
    if (formData.longitude < -180 || formData.longitude > 180) {
      alert(t('stores.validation.invalidLongitude') || 'Longitude must be between -180 and 180');
      return;
    }

    try {
      let updateData: any = {
        storeName: formData.name.trim(),
        manager: formData.manager.trim(),
        email: formData.email.trim(),
        storePhone: formData.phone.trim() || undefined,
        website: formData.website.trim() || undefined,
        storeType: formData.storeType,
        latitude: formData.latitude,
        longitude: formData.longitude,
        city: formData.city?.trim() || undefined,
        country: formData.country?.trim() || undefined,
        gpsValidation: {
          enabled: formData.gpsEnabled,
          radius: formData.gpsRadius,
          strictMode: false,
        },
        allowChipActivation: formData.allowChipActivation,
        allowQRActivation: formData.allowQRActivation,
      };

      if (formData.addressType === 'simple') {
        updateData.address = formData.simpleAddress.trim();
      } else {
        updateData.address = {
          street: formData.street.trim() || undefined,
          city: formData.city.trim() || undefined,
          state: formData.state.trim() || undefined,
          postalCode: formData.postalCode.trim() || undefined,
          country: formData.country || "US",
        };
      }

      console.log('✏️ Updating store:', editingStore.storeId, updateData);
      const response = await storeApi.updateStore(editingStore.storeId, updateData);
      
      if (response.success) {
        console.log("✅ Store updated successfully!");
        alert(`${t('stores.success.updated') || 'Store updated successfully'}!`);
        resetForm();
        setEditingStore(null);
        setShowEditModal(false);
        setRefreshKey(prev => prev + 1);
      } else {
        throw new Error(response.error || 'Failed to update store');
      }
      
    } catch (err: any) {
      console.error('❌ Error updating store:', err);
      alert(`${t('stores.error.updateFailed') || 'Failed to update store'}: ${err.message}`);
    }
  };

  const handleDelete = async (store: Store) => {
    const confirmDelete = window.confirm(
      t('stores.confirm.delete', { name: store.storeName }) || 
      `Are you sure you want to delete "${store.storeName}"?\n\nThis will deactivate the store and block all activations.`
    );

    if (!confirmDelete) return;

    try {
      console.log('🗑️ Deleting store:', store.storeId);
      
      const response = await storeApi.deleteStore(store.storeId);
      
      if (response.success) {
        console.log("✅ Store deleted/deactivated successfully!");
        alert(t('stores.success.deleted') || `Store deleted successfully!`);
        setRefreshKey(prev => prev + 1);
      } else {
        throw new Error(response.error || 'Failed to delete store');
      }
      
    } catch (err: any) {
      console.error('❌ Error deleting store:', err);
      alert(`${t('stores.error.deleteFailed') || 'Failed to delete store'}: ${err.message}`);
    }
  };

  const handleGPSToggle = async (store: Store) => {
    try {
      const newEnabled = !store.gpsValidation?.enabled;
      
      console.log(`📍 Toggling GPS for store ${store.storeId}: ${newEnabled}`);
      const response = await storeApi.updateGPSSettings(store.storeId, {
        enabled: newEnabled,
        radius: store.gpsValidation?.radius || 100,
        strictMode: false
      });
      
      if (response.success) {
        console.log(`✅ GPS ${newEnabled ? 'enabled' : 'disabled'} for store: ${store.storeId}`);
        setRefreshKey(prev => prev + 1);
      } else {
        throw new Error(response.error || 'Failed to update GPS settings');
      }
      
    } catch (err: any) {
      console.error('❌ Error updating GPS settings:', err);
      alert(`${t('stores.error.gpsFailed') || 'Failed to update GPS settings'}: ${err.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      manager: "",
      email: "",
      phone: "",
      website: "",
      storeType: "retailer",
      password: "",
      confirmPassword: "",
      requirePasswordChange: true,
      addressType: 'simple',
      simpleAddress: "",
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
      latitude: 0,
      longitude: 0,
      gpsEnabled: true,
      gpsRadius: 50000,
      allowChipActivation: true,
      allowQRActivation: true,
    });
  };

  const formatLocation = (store: Store) => {
    if (typeof store.address === 'object' && store.address) {
      const addr = store.address;
      let result = "";
      
      if (addr.street) result = addr.street;
      
      const cityState = [];
      if (addr.city) cityState.push(addr.city);
      if (addr.state) cityState.push(addr.state);
      if (addr.postalCode) cityState.push(addr.postalCode);
      
      if (cityState.length > 0) {
        result += result ? `, ${cityState.join(", ")}` : cityState.join(", ");
      }
      
      if (addr.country && addr.country !== 'US') {
        result += result ? `, ${addr.country}` : addr.country;
      }
      
      if (result) return result;
    }
    
    if (typeof store.address === 'string' && store.address.trim()) {
      return store.address.trim();
    }
    
    const parts = [];
    if (store.city) parts.push(store.city);
    if (store.state) parts.push(store.state);
    if (store.postalCode) parts.push(store.postalCode);
    if (store.country && store.country !== 'US') parts.push(store.country);
    
    if (parts.length > 0) return parts.join(", ");
    
    if (store.latitude && store.longitude) {
      return `📍 ${store.latitude.toFixed(4)}, ${store.longitude.toFixed(4)}`;
    }
    
    return "Location not set";
  };

  const handleManualRefresh = () => {
    setStores([]);
    setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">{t('stores.loading') || 'Loading stores...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t('stores.title') || 'Store Management'}</h2>
          <p className="text-gray-600">{t('stores.subtitle') || 'Manage retail locations and GPS validation settings'}</p>
          {error && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
              ⚠️ {error}
            </div>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleManualRefresh}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            🔄 {t('stores.refresh') || 'Refresh'}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + {t('stores.addStore') || 'Add Store'}
          </button>
        </div>
      </div>

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-gray-100 p-4 rounded-lg text-xs font-mono">
          <p>Debug Info:</p>
          <p>Stores Count: {stores.length}</p>
          <p>Loading: {loading ? 'true' : 'false'}</p>
          <p>Error: {error || 'none'}</p>
          <p>Business ID: {getCurrentBusinessId()}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-2xl font-bold text-blue-600">{stores.length}</div>
          <div className="text-sm text-gray-600">{t('stores.stats.total') || 'Total Stores'}</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-2xl font-bold text-green-600">
            {stores.filter(s => s.isActive).length}
          </div>
          <div className="text-sm text-gray-600">{t('stores.stats.active') || 'Active'}</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-2xl font-bold text-orange-600">
            {stores.filter(s => s.gpsValidation?.enabled).length}
          </div>
          <div className="text-sm text-gray-600">{t('stores.stats.gpsEnabled') || 'GPS Enabled'}</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-2xl font-bold text-purple-600">
            {stores.filter(s => (s as any).allowChipActivation).length}
          </div>
          <div className="text-sm text-gray-600">{t('stores.stats.chipEnabled') || 'Chip Enabled'}</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-2xl font-bold text-pink-600">
            {stores.filter(s => (s as any).allowQRActivation).length}
          </div>
          <div className="text-sm text-gray-600">{t('stores.stats.qrEnabled') || 'QR Enabled'}</div>
        </div>
      </div>

      {/* Stores Grid */}
      {stores.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store) => (
            <div key={String(store.storeId || store._id)} className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 truncate">{store.storeName}</h3>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    (store as any).storeType === 'flagship' ? 'bg-purple-100 text-purple-800' :
                    (store as any).storeType === 'factory' ? 'bg-orange-100 text-orange-800' :
                    (store as any).storeType === 'warehouse' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {(store as any).storeType || 'retailer'}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    store.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                  }`}>
                    {store.isActive ? t('stores.status.active') || 'Active' : t('stores.status.inactive') || 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <p><strong>{t('stores.storeId') || 'Store ID'}:</strong> <span className="font-mono text-xs">{String(store.storeId || (store as any)._id || '—')}</span></p>
                <p><strong>{t('stores.location') || 'Location'}:</strong> {formatLocation(store)}</p>
                <p><strong>{t('stores.manager') || 'Manager'}:</strong> {store.manager || t('stores.notAssigned') || "Not assigned"}</p>
                <p><strong>{t('stores.gps') || 'GPS'}:</strong> 
                  <span className={`ml-1 ${store.gpsValidation?.enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {store.gpsValidation?.enabled ? `${t('stores.enabled') || 'Enabled'} (${store.gpsValidation.radius}m)` : t('stores.disabled') || "Disabled"}
                  </span>
                </p>
                {(store as any).website && (
                  <p><strong>Website:</strong> <a href={(store as any).website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{(store as any).website}</a></p>
                )}
                <p><strong>{t('stores.coordinates') || 'Coordinates'}:</strong> {store.latitude?.toFixed(4)}, {store.longitude?.toFixed(4)}</p>
                <div className="flex justify-between text-xs">
                  <span>
                    <strong>{t('stores.chip') || 'Chip'}:</strong> 
                    <span className={`ml-1 ${(store as any).allowChipActivation ? 'text-green-600' : 'text-red-600'}`}>
                      {(store as any).allowChipActivation ? "✓" : "✗"}
                    </span>
                  </span>
                  <span>
                    <strong>{t('stores.qr') || 'QR'}:</strong> 
                    <span className={`ml-1 ${(store as any).allowQRActivation ? 'text-green-600' : 'text-red-600'}`}>
                      {(store as any).allowQRActivation ? "✓" : "✗"}
                    </span>
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex space-x-2">
                  <button 
                    onClick={() => setSelectedStore(store)} 
                    className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 text-sm transition-colors"
                  >
                    👁️ {t('stores.view') || 'View'}
                  </button>
                  <button
                    onClick={() => openEditModal(store)}
                    className="flex-1 bg-yellow-50 text-yellow-600 px-3 py-2 rounded-lg hover:bg-yellow-100 text-sm transition-colors"
                  >
                    ✏️ {t('stores.edit') || 'Edit'}
                  </button>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleGPSToggle(store)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      store.gpsValidation?.enabled
                        ? 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                  >
                    {store.gpsValidation?.enabled ? `📍 ${t('stores.gpsOff') || 'GPS Off'}` : `🗺️ ${t('stores.gpsOn') || 'GPS On'}`}
                  </button>
                  <button
                    onClick={() => handleDelete(store)}
                    className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 text-sm transition-colors"
                  >
                    🗑️ {t('stores.delete') || 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🏪</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('stores.noStores') || 'No stores found'}</h3>
          <p className="text-gray-600 mb-6">{t('stores.getStarted') || 'Get started by creating your first store location.'}</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + {t('stores.createFirst') || 'Create First Store'}
          </button>
        </div>
      )}

      {/* Create Store Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('stores.modal.addTitle') || 'Add New Store'}</h3>
            <form onSubmit={handleCreate} className="space-y-6">
              
              {/* Basic Store Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('stores.modal.basicInfo') || 'Basic Information'}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.storeName') || 'Store Name'} *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., VIETNAM, JEPTAGS, Downtown Store"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.storeType') || 'Store Type'} *</label>
                    <select
                      value={formData.storeType}
                      onChange={(e) => setFormData(prev => ({ ...prev, storeType: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">{t('stores.form.selectType') || 'Select store type...'}</option>
                      {STORE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.manager') || 'Manager'} *</label>
                    <input
                      type="text"
                      value={formData.manager}
                      onChange={(e) => setFormData(prev => ({ ...prev, manager: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., BIETZA, John Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.email') || 'Email'} *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., viet@gmail.com"
                      required
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.phone') || 'Phone'}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 46534634, +1-555-123-4567"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., https://agent-store.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional - useful for agents and distributors with their own website</p>
                </div>
              </div>

              {/* Password & Security Settings */}
              <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg">
                <div className="flex items-start space-x-3 mb-4">
                  <span className="text-2xl">🔐</span>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{t('stores.password.title') || 'Store Login Credentials'}</h4>
                    <p className="text-sm text-gray-600">{t('stores.password.subtitle') || 'Set a temporary password for the store manager'}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('stores.password.temporary') || 'Temporary Password'} * 
                      <span className="text-xs text-gray-500 ml-2">({t('stores.password.willChange') || 'Manager will change on first login'})</span>
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('stores.password.placeholder') || 'Min 8 chars, 1 uppercase, 1 number, 1 special char'}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.password.confirm') || 'Confirm Password'} *</label>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('stores.password.reenter') || 'Re-enter password'}
                      required
                    />
                    {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-red-600 text-sm mt-1">❌ {t('stores.password.noMatch') || 'Passwords do not match'}</p>
                    )}
                    {formData.password && formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <p className="text-green-600 text-sm mt-1">✅ {t('stores.password.match') || 'Passwords match'}</p>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-blue-900 mb-2">{t('stores.password.requirements') || 'Password Requirements'}:</p>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li className="flex items-center">
                        <span className={`mr-2 ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                          {formData.password.length >= 8 ? '✓' : '○'}
                        </span>
                        {t('stores.password.req1') || 'At least 8 characters'}
                      </li>
                      <li className="flex items-center">
                        <span className={`mr-2 ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          {/[A-Z]/.test(formData.password) ? '✓' : '○'}
                        </span>
                        {t('stores.password.req2') || 'One uppercase letter (A-Z)'}
                      </li>
                      <li className="flex items-center">
                        <span className={`mr-2 ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          {/[a-z]/.test(formData.password) ? '✓' : '○'}
                        </span>
                        {t('stores.password.req3') || 'One lowercase letter (a-z)'}
                      </li>
                      <li className="flex items-center">
                        <span className={`mr-2 ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          {/[0-9]/.test(formData.password) ? '✓' : '○'}
                        </span>
                        {t('stores.password.req4') || 'One number (0-9)'}
                      </li>
                      <li className="flex items-center">
                        <span className={`mr-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                          {/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? '✓' : '○'}
                        </span>
                        {t('stores.password.req5') || 'One special character (!@#$%...)'}
                      </li>
                    </ul>
                  </div>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.requirePasswordChange}
                      onChange={(e) => setFormData(prev => ({ ...prev, requirePasswordChange: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {t('stores.password.requireChange') || 'Require password change on first login'} ({t('stores.password.recommended') || 'Recommended'} ✅)
                    </span>
                  </label>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">
                      ⚠️ <strong>{t('stores.password.important') || 'Important'}:</strong> {t('stores.password.importantText', { manager: formData.manager || 'the store manager', email: formData.email || 'their email' }) || `After creating the store, you'll receive a Store ID and this temporary password. Share these credentials securely with ${formData.manager || 'the store manager'} at ${formData.email || 'their email'}.`}
                    </p>
                  </div>
                </div>
              </div>

              {/* GPS Coordinates */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('stores.modal.gpsCoordinates') || 'GPS Coordinates'}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.latitude') || 'Latitude'} *</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 16.0544"
                      min="-90"
                      max="90"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.longitude') || 'Longitude'} *</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 108.2022"
                      min="-180"
                      max="180"
                      required
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      autoDetectGPS(
                        (lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng })),
                        (msg) => alert('GPS Error: ' + msg + '. Please enter coordinates manually.')
                      );
                    }}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    📍 Auto-detect my location
                  </button>
                  <p className="text-xs text-gray-500 mt-2">Click to fill GPS from your device, or enter manually above</p>
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('stores.modal.address') || 'Address'}</h4>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.addressFormat') || 'Address Format'}</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="addressType"
                        value="simple"
                        checked={formData.addressType === 'simple'}
                        onChange={(e) => setFormData(prev => ({ ...prev, addressType: e.target.value as 'simple' | 'detailed' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">{t('stores.form.simpleAddress') || 'Simple (City/Region)'}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="addressType"
                        value="detailed"
                        checked={formData.addressType === 'detailed'}
                        onChange={(e) => setFormData(prev => ({ ...prev, addressType: e.target.value as 'simple' | 'detailed' }))}
                        className="mr-2"
                      />
                      <span className="text-sm">{t('stores.form.detailedAddress') || 'Detailed (Full address)'}</span>
                    </label>
                  </div>
                </div>

                {formData.addressType === 'simple' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.locationCity') || 'Location/City'}</label>
                    <input
                      type="text"
                      value={formData.simpleAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, simpleAddress: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., SAN JOSE, DANANG, Ho Chi Minh City"
                    />
                  </div>
                )}

                {formData.addressType === 'detailed' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.street') || 'Street Address'}</label>
                      <input
                        type="text"
                        value={formData.street}
                        onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 456 Market Street, Suite 200"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.city') || 'City'}</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., San Francisco"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.state') || 'State'}</label>
                        <input
                          type="text"
                          value={formData.state}
                          onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., CA"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.postalCode') || 'Postal Code'}</label>
                        <input
                          type="text"
                          value={formData.postalCode}
                          onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 94108"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.country') || 'Country'}</label>
                      <select
                        value={formData.country}
                        onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="US">United States</option>
                        <option value="USA">USA</option>
                        <option value="VN">Vietnam</option>
                        <option value="CA">Canada</option>
                        <option value="UK">United Kingdom</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* GPS & Activation Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{t('stores.modal.gpsSettings') || 'GPS Settings'}</h4>
                  <div className="space-y-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.gpsEnabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, gpsEnabled: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t('stores.form.enableGPS') || 'Enable GPS validation'}</span>
                    </label>
                    
                    {formData.gpsEnabled && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('stores.form.radius') || 'Radius'}</label>
                        <select
                          value={formData.gpsRadius}
                          onChange={(e) => setFormData(prev => ({ ...prev, gpsRadius: parseInt(e.target.value) }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value={100}>100m - Very Close</option>
                          <option value={500}>500m - Close</option>
                          <option value={1000}>1km - Nearby</option>
                          <option value={5000}>5km - Area</option>
                          <option value={10000}>10km - Wide Area</option>
                          <option value={50000}>50km - Regional (Default)</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{t('stores.modal.activationPermissions') || 'Activation Permissions'}</h4>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.allowChipActivation}
                        onChange={(e) => setFormData(prev => ({ ...prev, allowChipActivation: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t('stores.form.allowChip') || 'Allow Chip Activation'}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.allowQRActivation}
                        onChange={(e) => setFormData(prev => ({ ...prev, allowQRActivation: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t('stores.form.allowQR') || 'Allow QR Code Activation'}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('stores.cancel') || 'Cancel'}
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('stores.createStore') || 'Create Store'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Full */}
      {showEditModal && editingStore && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Store: {editingStore.storeName}</h3>
            <form onSubmit={handleUpdate} className="space-y-6">
              {/* Basic Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Store Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Store Type *</label>
                    <select value={formData.storeType} onChange={(e) => setFormData(prev => ({ ...prev, storeType: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required>
                      {STORE_TYPES.map(type => (<option key={type.value} value={type.value}>{type.label}</option>))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Manager *</label>
                    <input type="text" value={formData.manager} onChange={(e) => setFormData(prev => ({ ...prev, manager: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <input type="url" value={formData.website} onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="https://agent-store.com" />
                  </div>
                </div>
              </div>

              {/* GPS Coordinates */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">GPS Coordinates</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latitude *</label>
                    <input type="number" step="any" value={formData.latitude} onChange={(e) => setFormData(prev => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., 16.0544" min="-90" max="90" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitude *</label>
                    <input type="number" step="any" value={formData.longitude} onChange={(e) => setFormData(prev => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., 108.2022" min="-180" max="180" required />
                  </div>
                </div>
                <div className="mt-3">
                  <button type="button" onClick={() => { autoDetectGPS((lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng })), (msg) => alert('GPS Error: ' + msg)); }} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                    📍 Auto-detect my location
                  </button>
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input type="text" value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Danang" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                    <input type="text" value={formData.country} onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., VN" />
                  </div>
                </div>
              </div>

              {/* GPS & Activation Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">GPS Settings</h4>
                  <label className="flex items-center">
                    <input type="checkbox" checked={formData.gpsEnabled} onChange={(e) => setFormData(prev => ({ ...prev, gpsEnabled: e.target.checked }))} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <span className="ml-2 text-sm text-gray-700">Enable GPS validation</span>
                  </label>
                  {formData.gpsEnabled && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Radius</label>
                      <select value={formData.gpsRadius} onChange={(e) => setFormData(prev => ({ ...prev, gpsRadius: parseInt(e.target.value) }))} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                        <option value={100}>100m - Very Close</option>
                        <option value={500}>500m - Close</option>
                        <option value={1000}>1km - Nearby</option>
                        <option value={5000}>5km - Area</option>
                        <option value={10000}>10km - Wide Area</option>
                        <option value={50000}>50km - Regional (Default)</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Activation Permissions</h4>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input type="checkbox" checked={formData.allowChipActivation} onChange={(e) => setFormData(prev => ({ ...prev, allowChipActivation: e.target.checked }))} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="ml-2 text-sm text-gray-700">Allow NFC/Chip Activation</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" checked={formData.allowQRActivation} onChange={(e) => setFormData(prev => ({ ...prev, allowQRActivation: e.target.checked }))} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="ml-2 text-sm text-gray-700">Allow QR Activation</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button type="button" onClick={() => { setShowEditModal(false); setEditingStore(null); resetForm(); }} className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors">
                  Update Store
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Store Details Modal */}
      {selectedStore && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{selectedStore.storeName}</h3>
              <button onClick={() => setSelectedStore(null)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">{t('stores.details.storeInfo') || 'Store Information'}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">{t('stores.status.label') || 'Status'}:</span>
                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedStore.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedStore.isActive ? t('stores.status.active') || 'Active' : t('stores.status.inactive') || 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">{t('stores.type') || 'Type'}:</span>
                    <p className="mt-1">{(selectedStore as any).storeType || 'retailer'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">{t('stores.manager') || 'Manager'}:</span>
                    <p className="mt-1">{selectedStore.manager}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">{t('stores.email') || 'Email'}:</span>
                    <p className="mt-1">{selectedStore.email}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">{t('stores.phone') || 'Phone'}:</span>
                    <p className="mt-1">{selectedStore.storePhone || t('stores.notProvided') || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">{t('stores.businessId') || 'Business ID'}:</span>
                    <p className="font-mono mt-1">{String(selectedStore.businessId || '—')}</p>
                  </div>
                  {(selectedStore as any).website && (
                    <div className="col-span-2">
                      <span className="font-medium text-gray-700">Website:</span>
                      <p className="mt-1"><a href={(selectedStore as any).website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{(selectedStore as any).website}</a></p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">{t('stores.details.locationCoords') || 'Location & Coordinates'}</h4>
                <div className="text-sm space-y-2">
                  <p><strong>{t('stores.address') || 'Address'}:</strong> {formatLocation(selectedStore)}</p>
                  <p><strong>{t('stores.coordinates') || 'Coordinates'}:</strong> {
                    selectedStore.latitude && selectedStore.longitude 
                      ? `${selectedStore.latitude.toFixed(6)}, ${selectedStore.longitude.toFixed(6)}`
                      : t('stores.notSet') || 'Not set'
                  }</p>
                  <p><strong>{t('stores.storeId') || 'Store ID'}:</strong> <span className="font-mono">{String(selectedStore.storeId || '—')}</span></p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">{t('stores.details.settingsPermissions') || 'Settings & Permissions'}</h4>
                <div className="text-sm space-y-2">
                  <p><strong>{t('stores.gpsValidation') || 'GPS Validation'}:</strong> 
                    <span className={`ml-2 ${selectedStore.gpsValidation?.enabled ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedStore.gpsValidation?.enabled ? `${t('stores.enabled') || 'Enabled'} (${selectedStore.gpsValidation.radius}m)` : t('stores.disabled') || 'Disabled'}
                    </span>
                  </p>
                  <p><strong>{t('stores.chipActivation') || 'Chip Activation'}:</strong> 
                    <span className={`ml-2 ${(selectedStore as any).allowChipActivation ? 'text-green-600' : 'text-red-600'}`}>
                      {(selectedStore as any).allowChipActivation ? t('stores.allowed') || 'Allowed' : t('stores.blocked') || 'Blocked'}
                    </span>
                  </p>
                  <p><strong>{t('stores.qrActivation') || 'QR Activation'}:</strong> 
                    <span className={`ml-2 ${(selectedStore as any).allowQRActivation ? 'text-green-600' : 'text-red-600'}`}>
                      {(selectedStore as any).allowQRActivation ? t('stores.allowed') || 'Allowed' : t('stores.blocked') || 'Blocked'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setSelectedStore(null);
                  openEditModal(selectedStore);
                }}
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
              >
                {t('stores.editStore') || 'Edit Store'}
              </button>
              <button
                onClick={() => setSelectedStore(null)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                {t('stores.close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoresPage;